/**
 * Pending agenda writes. The queue stores the account id and the changed
 * fields so a closed page can retry. It never stores the access token.
 */

import { applyScheduleBody } from "./heuteScheduleMerge";

const STORAGE_KEY = "albis.pendingMutations";

const SCHEDULE_KEYS = ["done", "remind", "title", "date", "end_date", "time", "all_day", "place", "child", "visibility"];
const SCHOOL_KEYS = ["date", "time", "all_day", "title", "dismiss", "remind", "done", "child", "resolveChildAsk"];
const DEFINITIVE = new Set([400, 403, 404, 409, 422]);

const inFlight = new Set();
/** @type {Map<string, { kind: string, targetId: string, eventIndex: number|null, body: object, settledAt: number }>} */
const holds = new Map();

function pickBody(body, keys) {
  const next = {};
  if (!body || typeof body !== "object") return next;
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(body, key)) next[key] = body[key];
  }
  return next;
}

function readQueue() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry) => entry && entry.id && entry.kind && entry.body);
  } catch {
    return [];
  }
}

function writeQueue(entries) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
}

/** Account id already on this device. Does not copy the session token. */
export function cachedUserId() {
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith("sb-") || !key.endsWith("-auth-token")) continue;
      const raw = JSON.parse(localStorage.getItem(key) || "null");
      const id = raw?.user?.id || raw?.currentSession?.user?.id || null;
      if (id) return String(id);
    }
  } catch {
    /* ignore unreadable session storage */
  }
  return null;
}

/**
 * @param {{ kind: 'schedule'|'school-event', targetId: string, eventIndex?: number|null, body: object, userId?: string|null }} input
 */
export function beginPendingMutation(input) {
  const keys = input.kind === "school-event" ? SCHOOL_KEYS : SCHEDULE_KEYS;
  const entry = {
    id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    userId: input.userId || cachedUserId() || null,
    kind: input.kind,
    targetId: input.targetId,
    eventIndex: Number.isInteger(input.eventIndex) ? input.eventIndex : null,
    body: pickBody(input.body, keys),
    at: Date.now(),
  };
  writeQueue([...readQueue(), entry]);
  inFlight.add(entry.id);
  return entry.id;
}

export function stampPendingUser(pendingId, userId) {
  if (!pendingId || !userId) return;
  writeQueue(
    readQueue().map((entry) =>
      entry.id === pendingId && !entry.userId ? { ...entry, userId: String(userId) } : entry,
    ),
  );
}

function holdKey(hold) {
  if (hold.kind === "school-event") return `school:${hold.targetId}:${hold.eventIndex}`;
  return `schedule:${hold.targetId}`;
}

/**
 * @param {string|null} pendingId
 * @param {{ ok?: boolean, status?: number, hold?: { kind: string, targetId: string, eventIndex?: number|null, body: object } }} result
 */
export function finishPendingMutation(pendingId, result) {
  if (!pendingId) return;
  const definitive = DEFINITIVE.has(result?.status);
  if (result?.ok || definitive) {
    writeQueue(readQueue().filter((entry) => entry.id !== pendingId));
  }
  if (result?.ok && result.hold?.targetId) {
    const key = holdKey(result.hold);
    const prev = holds.get(key);
    holds.set(key, {
      kind: result.hold.kind,
      targetId: result.hold.targetId,
      eventIndex: Number.isInteger(result.hold.eventIndex) ? result.hold.eventIndex : null,
      body: { ...(prev?.body || {}), ...pickBody(result.hold.body, result.hold.kind === "school-event" ? SCHOOL_KEYS : SCHEDULE_KEYS) },
      settledAt: Date.now(),
    });
  }
  inFlight.delete(pendingId);
}

export function isPendingInFlight(pendingId) {
  return inFlight.has(pendingId);
}

export function claimPendingMutation(pendingId) {
  if (pendingId) inFlight.add(pendingId);
}

export function listPendingMutations(userId) {
  return readQueue().filter((entry) => !entry.userId || entry.userId === userId);
}

export function clearPendingMutations() {
  writeQueue([]);
  holds.clear();
  inFlight.clear();
}

export function isDefinitiveMutationError(err) {
  return DEFINITIVE.has(err?.status);
}

function matchesSchool(row, entry) {
  const sourceId = row?.sourceId || row?.mailExtractionId || null;
  const sourceIndex = Number.isInteger(row?.sourceIndex) ? row.sourceIndex : row?.eventIndex;
  return sourceId === entry.targetId && sourceIndex === entry.eventIndex;
}

function applyEntry(rows, entry) {
  if (entry.kind === "school-event") {
    if (entry.body?.dismiss || entry.body?.done) {
      return rows.filter((row) => !matchesSchool(row, entry));
    }
    return rows.map((row) => (matchesSchool(row, entry) ? applyScheduleBody(row, entry.body) : row));
  }
  if (entry.body?.done === true) return rows.filter((row) => row.id !== entry.targetId);
  return rows.map((row) => (row.id === entry.targetId ? applyScheduleBody(row, entry.body) : row));
}

/**
 * Keep edits that the server may not have stored yet on top of a fresh load.
 * A fetch that started after the save settled trusts the server.
 * @param {object[]} rows
 * @param {string|null} userId
 * @param {number} fetchStartedAt
 */
export function reconcileScheduleRows(rows, userId, fetchStartedAt) {
  let next = Array.isArray(rows) ? rows : [];
  const pending = readQueue().filter((entry) => !entry.userId || entry.userId === userId);
  for (const entry of pending) next = applyEntry(next, entry);
  for (const [key, hold] of holds) {
    if (fetchStartedAt >= hold.settledAt) {
      holds.delete(key);
      continue;
    }
    next = applyEntry(next, hold);
  }
  return next;
}
